/**
 * b-columns: Auto mobile order
 *
 * For 2-column layouts only: when the first column contains only media blocks
 * (cover, image, video…) and the second contains text, JS sets inline
 * style.order so media appears below text on mobile.
 *
 * Uses matchMedia at 991px — one pixel below $lg — so the swap never fires
 * in the desktop 2-column layout.
 *
 * Manual override: add has-mobile-order-* class to either column — JS skips
 * that pair entirely and CSS handles it via the @for loop in SCSS.
 */

const MOBILE_MQ = window.matchMedia( '(max-width: 991px)' );

const MEDIA_SELECTORS = [
	'.wp-block-cover',
	'.wp-block-image',
	'.wp-block-video',
	'.wp-block-embed',
	'.wp-block-gallery',
	'.wp-block-audio',
	'.wp-block-media-text',
	'video',
	'iframe',
];

/**
 * Returns true if any class on col starts with has-mobile-order-.
 *
 * @param {HTMLElement} col
 * @return {boolean}
 */
const hasManualOrder = ( col ) =>
	[ ...col.classList ].some( ( c ) => c.startsWith( 'has-mobile-order-' ) );

/**
 * Returns true if every direct child of col matches a media selector.
 *
 * @param {HTMLElement} col
 * @return {boolean}
 */
const isMediaColumn = ( col ) => {

	const children = [ ...col.children ];
	return children.length > 0 && children.every( ( child ) =>
		MEDIA_SELECTORS.some( ( sel ) => child.matches( sel ) )
	);

};

/**
 * Applies or removes inline order styles on each media/text column pair.
 *
 * @param {{ mediaCol: HTMLElement, textCol: HTMLElement }[]} pairs
 * @param {boolean} isMobile
 * @return {void}
 */
const applyOrder = ( pairs, isMobile ) => {

	pairs.forEach( ( { mediaCol, textCol } ) => {

		if ( isMobile ) {

			mediaCol.style.order = '2';
			textCol.style.order  = '1';

		} else {

			mediaCol.style.removeProperty( 'order' );
			textCol.style.removeProperty( 'order' );

		}

	} );

};

let activePairs = [];

MOBILE_MQ.addEventListener( 'change', ( e ) => applyOrder( activePairs, e.matches ) );

/**
 * Scans all 2-column blocks and registers pairs that need auto-ordering.
 *
 * @return {void}
 */
const init = () => {

	const pairs = [];

	document.querySelectorAll( '.b-columns__inner.has-2-columns' ).forEach( ( inner ) => {

		const cols = [ ...inner.children ].filter(
			( el ) => el.classList.contains( 'b-column' )
		);

		if ( cols.length !== 2 ) {

			return;

		}

		const [ col1, col2 ] = cols;

		if ( hasManualOrder( col1 ) || hasManualOrder( col2 ) ) {

			return;

		}

		// [media][text] → swap on mobile | [text][media] → correct order already
		if ( !isMediaColumn( col1 ) || isMediaColumn( col2 ) ) {

			return;

		}

		pairs.push( { mediaCol: col1, textCol: col2 } );

	} );

	activePairs = pairs;
	applyOrder( activePairs, MOBILE_MQ.matches );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', init );

} else {

	init();

}
